import type { CharacterDef, PassiveDef, RunResult, WeaponDef } from '../core/types';
import type { HudSnapshot } from '../game/game';
import type { LevelUpChoice } from '../game/levelup';
import type { Input } from '../core/input';
import { el, escapeHtml, formatTime, iconHtml } from './dom';
import { glyph } from './glyphs';
import { audio, type SoundMode } from '../core/audio';
import type { DisplayController } from '../core/display';
import { DisplayOptions } from './display-options';

/** 레벨업 카드 종류 딱지 [글, css 이름] — 1.23 부터 한 낱말만 */
const KIND_TAG: Record<LevelUpChoice['kind'], [string, string]> = {
  'weapon-new': ['새 무기', 'new'],
  'weapon-up': ['강화', 'up'],
  'passive-new': ['새 패시브', 'pnew'],
  'passive-up': ['강화', 'up'],
  heal: ['회복', 'heal'],
  fusion: ['조합', 'fusion'],
};

/** 캐릭터 한 장(프레임 번호)의 그림 경로 — 아틀라스 폴더의 frame_N.png (slice_character.py 가 같이 만든다) */
function frameSrc(c: CharacterDef, n: number): string | null {
  if (!c.atlas) return null;
  return `${c.atlas.slice(0, c.atlas.lastIndexOf('/'))}/frame_${n}.png`;
}

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));
/** 선택 화면 전신 도트 배율 (UI 전체 배율 --ui-scale 이 또 곱해진다) */
const HERO_SCALE = 2;

export interface UiHooks {
  onPickCharacter(id: string): void;
  onPickUpgrade(choice: LevelUpChoice): void;
  onResume(): void;
  onQuit(): void;
  onRetry(): void;
  onResubmit(): void;
  onPauseRequest(): void;
  /** i 번째 액티브 스킬 칸을 눌렀다 (폰은 이 버튼, PC 는 Space · E 도 된다) */
  onActive(i: number): void;
  /** 1.24 대시 칸을 눌렀다 (폰은 이 버튼, PC 는 Shift) */
  onDash(): void;
}

/** 화면(캐릭터 선택 / 레벨업 / 일시정지 / 결과)과 HUD를 전부 담당한다. */
export class Ui {
  private readonly displayOptions: DisplayOptions;
  private readonly root: HTMLElement;
  private readonly hud: HTMLElement;
  private readonly joystick: HTMLElement;
  private readonly joyRing: HTMLElement;
  private readonly joyKnob: HTMLElement;
  private readonly debug: HTMLElement;
  private readonly muteBadge: HTMLElement;

  private selectScreen!: HTMLElement;
  private levelScreen!: HTMLElement;
  private pauseScreen!: HTMLElement;
  private resultScreen!: HTMLElement;

  // HUD 내부 노드
  private elTime!: HTMLElement;
  private elNextBoss!: HTMLElement;
  private elNextBossWrap!: HTMLElement;
  private elLv!: HTMLElement;
  private elKills!: HTMLElement;
  private elXp!: HTMLElement;
  private elHp!: HTMLElement;
  private elHpText!: HTMLElement;
  private elBossWrap!: HTMLElement;
  private elBossName!: HTMLElement;
  private elBossBar!: HTMLElement;
  private elWeapons!: HTMLElement;
  private elPassives!: HTMLElement;
  /** 액티브 스킬 칸 둘 (0 = Space, 1 = E) */
  private readonly activeBtns: Array<{ btn: HTMLButtonElement; icon: HTMLElement; charges: HTMLElement; key: HTMLElement; last: string }> = [];
  private elShield!: HTMLElement;
  /** 1.24 대시 칸 */
  private dashBtn!: HTMLButtonElement;
  private dashIcon!: HTMLElement;
  private dashCharges!: HTMLElement;
  private lastDashZap = false;
  // 회광반조 기력 게이지 (화면 위)
  private elVigor!: HTMLElement;
  private elVigorBar!: HTMLElement;
  private elVigorNum!: HTMLElement;
  private elVigorCap!: HTMLElement;
  private lastVigorState = '';
  private readonly banner: HTMLElement;
  private bannerTimer = 0;

  private submitStatus!: HTMLElement;
  private resubmitBtn!: HTMLButtonElement;

  private lastItemsKey = '';
  /** 1.23 선택 카드의 기본 스킬·패시브 이름, 조합 재료 아이콘, 결과 화면 그림에 쓴다 (main.ts setCatalog) */
  private weaponDefs: readonly WeaponDef[] = [];
  private passiveDefs: readonly PassiveDef[] = [];
  private characters: readonly CharacterDef[] = [];
  /** 일시정지 화면에 지금 구성을 보여 주려고 마지막 HUD 를 들고 있는다 */
  private lastSnap: HudSnapshot | null = null;
  /** 선택 화면에서 걷고 있는 카드 */
  private walkTimer = 0;

  constructor(
    root: HTMLElement,
    private readonly hooks: UiHooks,
    private readonly devMode: boolean,
    display: DisplayController,
  ) {
    this.root = root;
    this.displayOptions = new DisplayOptions(root, display);

    this.hud = this.buildHud();
    this.joystick = el('div');
    this.joystick.id = 'joystick';
    this.joyRing = el('div', 'ring');
    this.joyKnob = el('div', 'knob');
    this.joystick.append(this.joyRing, this.joyKnob);
    this.joystick.hidden = true;

    this.debug = el('div');
    this.debug.id = 'debug';
    this.debug.hidden = !devMode;

    this.muteBadge = el('div', 'mute-badge', '소리 끔 · M');
    this.muteBadge.hidden = true;

    this.banner = el('div', 'banner');
    this.banner.hidden = true;

    this.selectScreen = el('div', 'overlay');
    this.levelScreen = el('div', 'overlay');
    this.pauseScreen = el('div', 'overlay');
    this.resultScreen = el('div', 'overlay');
    for (const s of [this.selectScreen, this.levelScreen, this.pauseScreen, this.resultScreen]) {
      s.hidden = true;
    }

    this.buildPause();
    this.buildResult();

    this.root.append(
      this.hud,
      this.joystick,
      this.debug,
      this.muteBadge,
      this.banner,
      this.selectScreen,
      this.levelScreen,
      this.pauseScreen,
      this.resultScreen,
    );
  }

  setCatalog(d: { weapons: readonly WeaponDef[]; passives: readonly PassiveDef[]; characters: readonly CharacterDef[] }): void {
    this.weaponDefs = d.weapons;
    this.passiveDefs = d.passives;
    this.characters = d.characters;
  }

  // ── HUD ────────────────────────────────────────────────

  private buildHud(): HTMLElement {
    const hud = el('div');
    hud.id = 'hud';
    hud.hidden = true;
    hud.innerHTML = `
      <div class="hud-center">
        <div class="hud-time" data-time>0:00</div>
        <div class="hud-nextboss" data-nextbosswrap>${glyph('crown', 2)}<span data-nextboss></span></div>
      </div>
      <div class="hud-top">
        <div class="hud-lv" data-lv>Lv.1</div>
        <div class="bar xp" style="flex:1"><i data-xp style="width:0%"></i></div>
        <div class="hud-kills" title="처치">${glyph('skull', 2)}<span data-kills>0</span></div>
      </div>
      <div class="hud-hp-wrap">
        <div class="bar hp" style="flex:1"><i data-hp style="width:100%"></i><i class="shield" data-shield style="width:0%"></i></div>
        <div class="hud-hp-text" data-hptext>100 / 100</div>
      </div>
      <div class="hud-vigor" data-vigor hidden>
        <div class="hud-vigor-row">
          <div class="vigor-name">기력</div>
          <div class="bar vigor" style="flex:1"><i data-vigorbar style="width:100%"></i></div>
          <div class="vigor-num" data-vigornum>100</div>
        </div>
        <div class="vigor-cap" data-vigorcap></div>
      </div>
      <div class="boss-wrap" data-bosswrap hidden>
        <div class="boss-name" data-bossname></div>
        <div class="bar boss"><i data-bossbar style="width:100%"></i></div>
      </div>
      <div class="hud-items">
        <div class="item-row" data-weapons></div>
        <div class="item-row" data-passives></div>
      </div>
      <button id="pause-btn" type="button" aria-label="일시정지">❚❚</button>
      <div class="active-row">
        <button class="active-btn dash-btn" type="button" data-dash hidden>
          <span class="active-icon"></span>
          <span class="active-cd"></span>
          <span class="active-charges"></span>
          <span class="active-key kbd">Shift</span>
        </button>
        ${[0, 1]
          .map(
            (i) => `<button class="active-btn" type="button" data-active="${i}" hidden>
          <span class="active-icon"></span>
          <span class="active-cd"></span>
          <span class="active-charges"></span>
          <span class="active-key"></span>
        </button>`,
          )
          .join('')}
      </div>
    `;

    const q = <T extends HTMLElement>(sel: string): T => hud.querySelector(sel) as T;
    this.elTime = q('[data-time]');
    this.elNextBoss = q('[data-nextboss]');
    this.elNextBossWrap = q('[data-nextbosswrap]');
    this.elLv = q('[data-lv]');
    this.elKills = q('[data-kills]');
    this.elXp = q('[data-xp]');
    this.elHp = q('[data-hp]');
    this.elHpText = q('[data-hptext]');
    this.elBossWrap = q('[data-bosswrap]');
    this.elBossName = q('[data-bossname]');
    this.elBossBar = q('[data-bossbar]');
    this.elWeapons = q('[data-weapons]');
    this.elPassives = q('[data-passives]');
    this.elShield = q('[data-shield]');
    this.elVigor = q('[data-vigor]');
    this.elVigorBar = q('[data-vigorbar]');
    this.elVigorNum = q('[data-vigornum]');
    this.elVigorCap = q('[data-vigorcap]');

    q<HTMLButtonElement>('#pause-btn').addEventListener('click', () => this.hooks.onPauseRequest());
    // 1.24 대시 칸 — 폰은 이 버튼, PC 는 Shift
    this.dashBtn = q<HTMLButtonElement>('[data-dash]');
    this.dashIcon = this.dashBtn.querySelector('.active-icon') as HTMLElement;
    this.dashCharges = this.dashBtn.querySelector('.active-charges') as HTMLElement;
    this.dashIcon.innerHTML = glyph('speed', 3);
    this.dashBtn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.hooks.onDash();
    });
    // 폰: 손가락을 대는 순간 쓴다(떼기를 기다리면 늦다). 조이스틱을 잡은 손과 다른 손가락이어도 된다.
    // 대시 칸은 위에서 따로 이었다 — 여기 섞이면 액티브 스킬이 그 칸을 덮어쓴다(1.24)
    hud.querySelectorAll<HTMLButtonElement>('.active-btn:not(.dash-btn)').forEach((btn, i) => {
      this.activeBtns.push({
        btn,
        icon: btn.querySelector('.active-icon') as HTMLElement,
        charges: btn.querySelector('.active-charges') as HTMLElement,
        key: btn.querySelector('.active-key') as HTMLElement,
        last: '',
      });
      btn.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.hooks.onActive(i);
      });
    });
    return hud;
  }

  setHudVisible(v: boolean): void {
    this.hud.hidden = !v;
  }

  updateHud(s: HudSnapshot): void {
    this.elTime.textContent = formatTime(s.elapsed);
    this.elLv.textContent = `Lv.${s.level}`;
    this.lastSnap = s;
    this.elKills.textContent = s.kills.toLocaleString('ko-KR');
    // 보스가 나와 있으면 카운트다운 대신 보스 바를 본다 (왕관 = 다음 보스까지)
    this.elNextBossWrap.hidden = !!s.bossName;
    if (!s.bossName) this.elNextBoss.textContent = formatTime(s.nextBossIn);
    this.elXp.style.width = `${Math.min(100, (s.xp / Math.max(1, s.xpNeed)) * 100).toFixed(1)}%`;
    const hpPct = Math.min(100, (s.hp / Math.max(1, s.maxHp)) * 100);
    this.elHp.style.width = `${hpPct.toFixed(1)}%`;
    // 세포 역설 보호막: 체력 바 끝에 이어 붙인 옅은 초록
    const shPct = Math.min(100 - hpPct, (s.shield / Math.max(1, s.maxHp)) * 100);
    this.elShield.style.left = `${hpPct.toFixed(1)}%`;
    this.elShield.style.width = `${Math.max(0, shPct).toFixed(1)}%`;
    this.elHpText.textContent =
      s.shield >= 1 ? `${Math.ceil(s.hp)} +${Math.floor(s.shield)} / ${Math.round(s.maxHp)}` : `${Math.ceil(s.hp)} / ${Math.round(s.maxHp)}`;

    if (s.bossName) {
      this.elBossWrap.hidden = false;
      this.elBossName.textContent = s.bossName;
      this.elBossBar.style.width = `${(s.bossHpRatio * 100).toFixed(1)}%`;
    } else {
      this.elBossWrap.hidden = true;
    }

    // 무기/패시브 목록은 바뀔 때만 다시 그린다
    const key = `${s.weapons.map((w) => w.name + w.level).join(',')}|${s.passives
      .map((p) => p.name + p.level)
      .join(',')}`;
    if (key !== this.lastItemsKey) {
      this.lastItemsKey = key;
      this.elWeapons.innerHTML = s.weapons
        .map(
          (w) =>
            `<div class="item-chip" title="${escapeHtml(w.name)} Lv.${w.level}">${iconHtml(
              w.shape,
              w.color,
              w.sprite,
              18,
            )}<span>${w.level}</span></div>`,
        )
        .join('');
      this.elPassives.innerHTML = s.passives
        .map(
          (p) =>
            `<div class="item-chip" title="${escapeHtml(p.name)} Lv.${p.level}">${iconHtml(
              p.shape,
              p.color,
              p.sprite,
              18,
            )}<span>${p.level}</span></div>`,
        )
        .join('');
    }

    // 1.24 대시 칸 — 쿨타임만큼 어둠이 걷힌다. 전격 질주를 가지면 보랏빛 번개 칸이 된다
    const dv = s.dash;
    this.dashBtn.hidden = false;
    this.dashBtn.style.setProperty('--p', dv.charges > 0 ? '1' : dv.ratio.toFixed(3));
    this.dashBtn.classList.toggle('ready', dv.charges > 0);
    this.dashBtn.classList.toggle('zap', dv.zap);
    this.dashCharges.textContent = dv.max > 1 ? String(dv.charges) : '';
    if (dv.zap !== this.lastDashZap) {
      this.lastDashZap = dv.zap;
      this.dashIcon.innerHTML = glyph(dv.zap ? 'bolt' : 'speed', 3);
      this.dashBtn.title = dv.zap ? '전격 질주 — Shift: 번개가 되어 가른다' : '대시 — Shift';
      this.dashBtn.setAttribute('aria-label', dv.zap ? '전격 질주 대시' : '대시');
    }

    // 액티브 스킬 칸: 쿨타임은 버튼을 덮은 어둠이 시계 방향으로 걷히는 것으로 보여 준다.
    // 변신(회광반조)은 기력만큼 걷혀 있고, 변신한 동안은 테두리가 타오르며 기력이 닳는 만큼 다시 덮인다
    this.activeBtns.forEach((b, i) => {
      const act = s.actives[i];
      b.btn.hidden = !act;
      if (!act) return;
      const key = `${act.name}|${act.color}|${act.shape}|${act.key}`;
      if (key !== b.last) {
        b.last = key;
        b.icon.innerHTML = iconHtml(act.shape, act.color, act.sprite, 30);
        b.btn.title = act.kind === 'form' ? `${act.name} — ${act.key}: 변신 / 다시 누르면 풀림` : `${act.name} — ${act.key}`;
        b.btn.setAttribute('aria-label', `${act.name} (${act.key})`);
        b.key.textContent = act.key;
        b.btn.classList.toggle('form', act.kind === 'form');
      }
      b.btn.style.setProperty('--p', act.kind === 'leap' && act.charges > 0 ? '1' : act.ratio.toFixed(3));
      b.btn.classList.toggle('ready', act.charges > 0);
      b.btn.classList.toggle('on', act.on);
      b.btn.classList.toggle('tired', act.tired);
      // 연속 사냥 창이 열려 있으면 다음 도약이 몇 단째인지, 아니면 모아 둔 횟수
      b.charges.textContent = act.combo > 0 ? `×${act.combo + 1}` : act.max > 1 ? String(act.charges) : '';
      b.btn.classList.toggle('combo', act.combo > 0);
    });

    // 회광반조 기력 — 변신한 동안 닳고, 다 닳으면 지쳐서 풀린다
    const vg = s.vigor;
    this.elVigor.hidden = !vg;
    if (vg) {
      const v = Math.max(0, Math.min(100, vg.value));
      this.elVigorBar.style.width = `${v.toFixed(1)}%`;
      this.elVigorNum.textContent = String(Math.ceil(v));
      const st = vg.on ? (v < 25 ? 'on low' : 'on') : vg.tired ? 'tired' : vg.ready ? 'ready' : 'rest';
      if (st + vg.key !== this.lastVigorState) {
        this.lastVigorState = st + vg.key;
        this.elVigor.className = `hud-vigor ${st}`;
        // 1.23 한두 낱말로 — 키(폰은 버튼)와 할 일만. 되찾는 중에는 막대만 본다
        const k = `<span class="kbd"><b>${vg.key}</b></span><span class="touch"><b>버튼</b></span>`;
        this.elVigorCap.innerHTML = vg.on ? `${k} 풀기` : vg.tired ? '지쳤다' : vg.ready ? `${k} 변신` : '';
      }
    }

    if (this.devMode) {
      this.debug.textContent = `FPS ${s.fps.toFixed(0)}\n적 ${s.enemyCount}\n경과 ${formatTime(s.elapsed)}`;
    }
  }

  // ── 가상 조이스틱 ──────────────────────────────────────

  updateJoystick(input: Input): void {
    const st = input.stick;
    if (!st.active) {
      this.joystick.hidden = true;
      return;
    }
    this.joystick.hidden = false;
    this.joyRing.style.left = `${st.ox}px`;
    this.joyRing.style.top = `${st.oy}px`;
    let dx = st.kx - st.ox;
    let dy = st.ky - st.oy;
    const len = Math.hypot(dx, dy);
    if (len > st.radius) {
      dx = (dx / len) * st.radius;
      dy = (dy / len) * st.radius;
    }
    this.joyKnob.style.left = `${st.ox + dx}px`;
    this.joyKnob.style.top = `${st.oy + dy}px`;
  }

  // ── 캐릭터 선택 ────────────────────────────────────────
  // 1.23: 설명 글은 뺐다 — 전신 도트(올리면 걷는다) · 이름 · 기본 스킬/패시브 이름 · 특성 도트 아이콘 · 체력/속도 막대만.
  // 숫자·풀이는 올리면 뜨는 title 에만 둔다

  showCharacterSelect(chars: readonly CharacterDef[]): void {
    this.hideAll();
    this.stopWalk();
    this.selectScreen.innerHTML = `
      <div class="select-head">
        <div class="logo"><span class="logo-a">살바트리스</span><span class="logo-b">서바이버즈</span></div>
        <button class="btn ghost select-options" type="button" data-options>옵션</button>
      </div>
      <div class="hero-grid">${chars.map((c, i) => this.heroCard(c, i)).join('')}</div>
      <div class="select-hint" aria-hidden="true">
        <span class="kbd">${['W', 'A', 'S', 'D'].map((k) => `<i class="key">${k}</i>`).join('')}</span>
        <span class="touch">${glyph('finger', 3)}</span>
      </div>
    `;
    this.selectScreen.querySelector('[data-options]')!.addEventListener('click', () => this.displayOptions.open());
    this.selectScreen.querySelectorAll<HTMLButtonElement>('.hero').forEach((btn) => {
      const c = chars[Number(btn.dataset.i)];
      const img = btn.querySelector<HTMLImageElement>('.hero-sprite');
      if (img && c) {
        this.placeFrame(img, c, c.anim?.idleDown[0] ?? 0);
        const walk = (): void => this.startWalk(img, c);
        const stand = (): void => {
          this.stopWalk();
          this.placeFrame(img, c, c.anim?.idleDown[0] ?? 0);
        };
        btn.addEventListener('pointerenter', walk);
        btn.addEventListener('focus', walk);
        btn.addEventListener('pointerleave', stand);
        btn.addEventListener('blur', stand);
      }
      btn.addEventListener('click', () => {
        audio.play('ui');
        this.stopWalk();
        this.hooks.onPickCharacter(btn.dataset.id ?? '');
      });
    });
    this.selectScreen.hidden = false;
    this.setHudVisible(false);
  }

  /** 숫자 키로 캐릭터 고르기 (선택 화면에서만) */
  pickCharacterByIndex(i: number): boolean {
    if (this.selectScreen.hidden) return false;
    const btn = this.selectScreen.querySelector<HTMLButtonElement>(`.hero[data-i="${i}"]`);
    if (!btn) return false;
    btn.click();
    return true;
  }

  private heroCard(c: CharacterDef, i: number): string {
    const w = c.startWeapon ? this.weaponDefs.find((d) => d.id === c.startWeapon) : undefined;
    const p = c.passive ? this.passiveDefs.find((d) => d.id === c.passive) : undefined;
    const kit = (name: string, shape: string, color: string, sprite: string | undefined, cls: string, tip: string): string =>
      `<span class="kit ${cls}" title="${escapeHtml(tip)}">${iconHtml(shape, color, sprite, 14)}<span>${escapeHtml(name)}</span></span>`;
    const pct = (v: number): string => `${v > 0 ? '+' : '−'}${Math.round(Math.abs(v) * 100)}%`;
    // 특성은 도트 기호 대신 한글로 적는다 — 기호만으로는 무슨 뜻인지 모르겠다는 사람이 많았다(폰은 설명 풍선도 안 뜬다)
    const t = c.trait ?? {};
    const traits: Array<{ label: string; value: string; tip: string; neg: boolean }> = [];
    if (t.slash) traits.push({ label: '베기 피해', value: pct(t.slash), tip: '근접·대검 계열 무기의 피해', neg: t.slash < 0 });
    if (t.magic) traits.push({ label: '마법 피해', value: pct(t.magic), tip: '투사체·장판·궤도 등 마법 계열 무기의 피해', neg: t.magic < 0 });
    if (t.damage) traits.push({ label: '모든 피해', value: pct(t.damage), tip: '모든 무기의 피해', neg: t.damage < 0 });
    if (t.xp) traits.push({ label: '경험치', value: pct(t.xp), tip: '얻는 경험치', neg: t.xp < 0 });
    if (t.choices && t.choices !== 3) traits.push({ label: '레벨업 카드', value: `${t.choices}장`, tip: '레벨업 때 고를 수 있는 카드 수 (보통 3장)', neg: false });
    const src = frameSrc(c, c.anim?.idleDown[0] ?? 0);
    const art = src
      ? `<img class="hero-sprite" src="${escapeHtml(src)}" alt="" draggable="false">`
      : `<span class="hero-shape">${iconHtml(c.shape, c.color, c.sprite, 64)}</span>`;
    // 막대는 여덟 명 사이의 차이가 보이게 잘라서 (체력 90~190 · 속도 150~215)
    const hpK = clamp01((c.maxHp - 90) / 100);
    const spK = clamp01((c.moveSpeed - 150) / 65);
    return `
      <button class="hero" type="button" data-id="${escapeHtml(c.id)}" data-i="${i}" style="--c:${escapeHtml(c.color)}" aria-label="${escapeHtml(c.name)}">
        <span class="hero-key kbd">${i + 1}</span>
        <span class="hero-stage">${art}</span>
        <span class="hero-name">${escapeHtml(c.name)}</span>
        <span class="hero-kit">
          ${w ? kit(w.name, w.shape, w.color, w.sprite, 'weapon', w.description) : ''}
          ${p ? kit(p.name, p.shape, p.color, p.sprite, 'passive', p.description) : ''}
        </span>
        ${traits.length ? `<span class="hero-traits">${traits.map((x) => `<span class="trait${x.neg ? ' neg' : ''}" title="${escapeHtml(x.tip)}">${escapeHtml(x.label)} <b>${escapeHtml(x.value)}</b></span>`).join('')}</span>` : ''}
        <span class="hero-meters">
          <span class="meter hp" title="체력 ${c.maxHp}">${glyph('heart', 2)}<i><b style="width:${(hpK * 100).toFixed(0)}%"></b></i></span>
          <span class="meter spd" title="속도 ${c.moveSpeed}">${glyph('speed', 2)}<i><b style="width:${(spK * 100).toFixed(0)}%"></b></i></span>
        </span>
      </button>`;
  }

  /** 한 장을 발 기준으로 세운다 — 발 가로 위치(anchorX)·왼쪽을 보고 그려진 장(mirror)을 맞춘다 */
  private placeFrame(img: HTMLImageElement, c: CharacterDef, n: number): void {
    const src = frameSrc(c, n);
    if (!src) return;
    const ax = c.anim?.anchorX?.[String(n)] ?? 0.5;
    const flip = c.anim?.mirror?.includes(n) ?? false;
    const size = (): void => {
      img.width = img.naturalWidth * HERO_SCALE;
      img.height = img.naturalHeight * HERO_SCALE;
    };
    img.onload = size;
    if (!img.src.endsWith(src)) img.src = src;
    if (img.complete && img.naturalWidth) size();
    // 뒤집힌 장은 발 위치도 뒤집힌다
    img.style.transform = `translateX(${(-(flip ? 1 - ax : ax) * 100).toFixed(1)}%) scaleX(${flip ? -1 : 1})`;
  }

  private startWalk(img: HTMLImageElement, c: CharacterDef): void {
    this.stopWalk();
    const frames = c.anim?.walkSide ?? [];
    if (frames.length < 2) return;
    for (const n of frames) {
      const pre = new Image();
      pre.src = frameSrc(c, n) ?? '';
    }
    let k = 0;
    this.walkTimer = window.setInterval(() => {
      k = (k + 1) % frames.length;
      this.placeFrame(img, c, frames[k]);
    }, 95);
    this.placeFrame(img, c, frames[0]);
  }

  private stopWalk(): void {
    if (this.walkTimer) window.clearInterval(this.walkTimer);
    this.walkTimer = 0;
  }

  // ── 레벨업 ─────────────────────────────────────────────
  // 1.23: 안내 문장을 빼고, 카드마다 숫자 키 · 종류 딱지(한 낱말) · 레벨 칸으로. 조합 카드는 사라지는 재료를 아이콘으로 보인다

  showLevelUp(choices: readonly LevelUpChoice[], level: number, source: 'level' | 'chest' = 'level'): void {
    const chest = source === 'chest';
    this.levelScreen.classList.toggle('chest', chest);
    this.levelScreen.innerHTML = `
      <div class="lvl-head">${
        chest
          ? `<span class="lvl-chest">${glyph('chest', 4)}</span><span class="lvl-title">보물 상자</span>`
          : `<span class="lvl-cap">레벨</span><span class="lvl-num">${level}</span>`
      }</div>
      <div class="pick-grid n${choices.length}">${choices.map((c, i) => this.pickCard(c, i)).join('')}</div>
    `;
    this.levelScreen.querySelectorAll<HTMLButtonElement>('.pick').forEach((btn) => {
      btn.addEventListener('click', () => {
        const c = choices[Number(btn.dataset.i)];
        if (c.branches?.length && !c.pick) {
          this.showBranch(c, choices, level, source);
          return;
        }
        // 1.25 조합 카드는 바로 합치지 않고 "무엇을 합칠까" 를 먼저 묻는다
        if (c.kind === 'fusion' && (c.options?.length ?? 0) > 0) {
          this.showFusionPick(c, choices, level, source);
          return;
        }
        this.levelScreen.hidden = true;
        this.hooks.onPickUpgrade(c);
      });
    });
    this.levelScreen.hidden = false;
  }

  private pickCard(c: LevelUpChoice, i: number): string {
    const fork = !!c.branches?.length && !c.pick;
    const [tag, kind] = fork ? ['갈림길', 'fork'] : KIND_TAG[c.kind];
    let body: string;
    if (fork) {
      body = `<span class="pick-forks">${(c.branches ?? []).map((b) => `<span class="fork-name">${escapeHtml(b.name)}</span>`).join('')}</span>`;
    } else if (c.kind === 'fusion') {
      // 1.25 재료는 다음 화면에서 고른다 — 카드에는 무엇이 필요한지만 적는다
      const desc = c.desc.split('\n\n▸')[0];
      const need = c.need ?? 3;
      const req = c.reqLabel ? ` · ${escapeHtml(c.reqLabel)} ${c.needMatch ?? 1}` : '';
      body =
        `<span class="pick-desc">${escapeHtml(desc)}</span>` +
        `<span class="pick-gone"><span class="gone-pick">재료 ${need}개 고르기${req}</span>` +
        `<span class="gone-arrow">${glyph('play', 2)}</span>${iconHtml(c.shape, c.color, c.sprite, 22)}</span>`;
    } else {
      body = `<span class="pick-desc">${escapeHtml(c.desc)}</span>`;
    }
    const pips =
      c.maxLevel > 0
        ? `<span class="pips" title="Lv.${c.level} / ${c.maxLevel}">${Array.from(
            { length: c.maxLevel },
            (_, k) => `<i class="${k < c.level - 1 ? 'on' : k === c.level - 1 ? 'new' : ''}"></i>`,
          ).join('')}</span>`
        : '';
    return `
      <button class="pick ${kind}" type="button" data-i="${i}" style="--c:${escapeHtml(c.color)}">
        <span class="pick-key kbd">${i + 1}</span>
        <span class="pick-top"><span class="pick-icon">${iconHtml(c.shape, c.color, c.sprite, 30)}</span><span class="pick-tag">${tag}</span></span>
        <span class="pick-name">${escapeHtml(c.name)}</span>
        ${body}
        ${pips}
      </button>`;
  }

  /** 진화 갈림길: 두 갈래 중 하나를 고른다. 되돌아가서 다른 카드를 고를 수도 있다. */
  private showBranch(c: LevelUpChoice, choices: readonly LevelUpChoice[], level: number, source: 'level' | 'chest'): void {
    const branches = c.branches ?? [];
    this.levelScreen.innerHTML = `
      <div class="lvl-head fork-head">
        <span class="pick-icon">${iconHtml(c.shape, c.color, c.sprite, 34)}</span>
        <span class="lvl-title">${escapeHtml(c.name)}</span>
        <span class="lvl-sub">Lv.${c.level} 진화</span>
      </div>
      <div class="pick-grid n${branches.length}">
        ${branches
          .map(
            (b, i) => `
          <button class="pick fork opt" type="button" data-i="${i}" style="--c:${escapeHtml(c.color)}">
            <span class="pick-key kbd">${i + 1}</span>
            <span class="pick-name">${escapeHtml(b.name)}</span>
            <span class="pick-desc">${escapeHtml(b.desc)}</span>
          </button>`,
          )
          .join('')}
      </div>
      <button class="btn ghost back-btn" type="button" data-back>${glyph('back', 2)}<span>다른 카드</span></button>
    `;
    this.levelScreen.querySelectorAll<HTMLButtonElement>('.pick').forEach((btn) => {
      btn.addEventListener('click', () => {
        const b = branches[Number(btn.dataset.i)];
        this.levelScreen.hidden = true;
        this.hooks.onPickUpgrade({ ...c, pick: b.id });
      });
    });
    this.levelScreen
      .querySelector<HTMLButtonElement>('[data-back]')!
      .addEventListener('click', () => this.showLevelUp(choices, level, source));
  }

  /**
   * 1.25 조합 재료 고르기 — 가진 재료(6레벨 이상, 조합물 아님) 중에서 무엇을 합칠지 직접 고른다.
   * 확인 단추는 없다. 필요한 수(보통 셋)를 다 고르는 순간 바로 합쳐진다.
   * 그래서 "고르면 못 합치는 조합"이 되지 않게, 조건(황색 하나 등)을 못 채우게 될 칸은 미리 잠근다 —
   * 마지막 한 자리가 남았는데 조건이 아직 모자라면 조건에 맞는 칸만 눌린다.
   */
  private showFusionPick(c: LevelUpChoice, choices: readonly LevelUpChoice[], level: number, source: 'level' | 'chest'): void {
    const options = c.options ?? [];
    const need = c.need ?? options.length;
    const needMatch = c.needMatch ?? 0;
    const picked = new Set<string>();
    const req = c.reqLabel
      ? `재료 ${need}개 · 그중 ${needMatch}개는 <b>${escapeHtml(c.reqLabel)}</b>`
      : `재료 ${need}개`;

    this.levelScreen.innerHTML = `
      <div class="lvl-head fork-head">
        <span class="pick-icon">${iconHtml(c.shape, c.color, c.sprite, 34)}</span>
        <span class="lvl-title">${escapeHtml(c.name)}</span>
        <span class="lvl-sub">무엇을 합칠까 — ${req}</span>
      </div>
      <div class="mat-grid">
        ${options
          .map(
            (o, i) => `
          <button class="mat${o.matches ? ' fits' : ''}" type="button" data-i="${i}" data-id="${escapeHtml(o.id)}"
                  style="--c:${escapeHtml(o.color)}" aria-pressed="false">
            <span class="pick-key kbd">${i + 1}</span>
            <span class="mat-icon">${iconHtml(o.shape, o.color, o.sprite, 26)}</span>
            <span class="mat-name">${escapeHtml(o.name)}</span>
            <span class="mat-lv">Lv.${o.level}</span>
            ${o.matches && c.reqLabel ? `<span class="mat-fits">${escapeHtml(c.reqLabel)}</span>` : ''}
          </button>`,
          )
          .join('')}
      </div>
      <div class="mat-bar"><span class="mat-count" data-count></span></div>
      <button class="btn ghost back-btn" type="button" data-back>${glyph('back', 2)}<span>다른 카드</span></button>
    `;

    const countEl = this.levelScreen.querySelector<HTMLElement>('[data-count]')!;
    const btns = [...this.levelScreen.querySelectorAll<HTMLButtonElement>('.mat')];
    const byId = new Map(options.map((o) => [o.id, o]));

    /** 남은 자리로는 조건을 못 채우게 되는가 — 그러면 조건에 안 맞는 칸을 잠근다 */
    const lockedNow = (): boolean => {
      const short = needMatch - options.filter((o) => picked.has(o.id) && o.matches).length;
      return need - picked.size <= short;
    };

    const refresh = (): void => {
      const lock = lockedNow();
      for (const b of btns) {
        const o = byId.get(b.dataset.id!)!;
        const on = picked.has(o.id);
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', String(on));
        b.classList.toggle('locked', !on && lock && !o.matches);
      }
      const left = need - picked.size;
      countEl.innerHTML = lock
        ? `<b>${left}</b>개 더 — 이제 <b>${escapeHtml(c.reqLabel ?? '')}</b>만 고를 수 있다`
        : `<b>${left}</b>개 더 고른다`;
    };

    for (const b of btns) {
      b.addEventListener('click', () => {
        const o = byId.get(b.dataset.id!)!;
        if (picked.has(o.id)) {
          picked.delete(o.id);
          refresh();
          return;
        }
        if (!o.matches && lockedNow()) {
          // 이걸 넣으면 조건을 못 채운다 — 흔들어서 알린다
          b.classList.remove('nope');
          void b.offsetWidth;
          b.classList.add('nope');
          return;
        }
        picked.add(o.id);
        if (picked.size >= need) {
          // 다 골랐다 — 바로 합친다
          this.levelScreen.hidden = true;
          this.hooks.onPickUpgrade({ ...c, consumes: [...picked] });
          return;
        }
        refresh();
      });
    }

    this.levelScreen
      .querySelector<HTMLButtonElement>('[data-back]')!
      .addEventListener('click', () => this.showLevelUp(choices, level, source));

    refresh();
    this.levelScreen.hidden = false;
  }


  /** 숫자 키로 레벨업 카드 선택. 재료 고르기 화면에서는 재료를 고른다(마지막 하나를 고르면 바로 합쳐진다). */
  pickUpgradeByIndex(i: number): boolean {
    if (this.levelScreen.hidden) return false;
    const btn = this.levelScreen.querySelector<HTMLButtonElement>(`.pick[data-i="${i}"], .mat[data-i="${i}"]`);
    if (!btn) return false;
    btn.click();
    return true;
  }

  // ── 일시정지 ───────────────────────────────────────────
  // 1.23: 안내 문장을 빼고, 지금 들고 있는 무기·패시브를 이름·레벨로 보여 준다(HUD 는 아이콘뿐이라). 소리는 도트 아이콘 + 막대

  private buildPause(): void {
    this.pauseScreen.innerHTML = `
      <div class="pause-box">
        <h1 class="pause-title">일시정지</h1>
        <div class="pause-kit" data-kit></div>
        <div class="audio-row">
          <button class="sound-btn" type="button" data-mute aria-label="소리 모드"></button>
          <input class="vol" type="range" min="0" max="100" step="5" data-vol aria-label="전체 음량" />
          <span class="vol-num" data-volnum>70</span>
          <span class="mode-label" data-modelabel>전부</span>
        </div>
        <div class="audio-row">
          <span class="sound-btn" aria-hidden="true">${glyph('note', 2)}</span>
          <input class="vol" type="range" min="0" max="100" step="5" data-music aria-label="음악 음량" />
          <span class="vol-num" data-musicnum>50</span>
          <span class="mode-label">음악</span>
        </div>
        <div class="result-actions">
          <button class="btn" type="button" data-resume>${glyph('play', 2)}<span>계속</span></button>
          <button class="btn ghost" type="button" data-options>옵션</button>
          <button class="btn ghost" type="button" data-quit>나가기</button>
        </div>
      </div>
    `;

    this.pauseScreen.querySelector('[data-options]')!.addEventListener('click', () => this.displayOptions.open());
    const vol = this.pauseScreen.querySelector<HTMLInputElement>('[data-vol]')!;
    const num = this.pauseScreen.querySelector<HTMLElement>('[data-volnum]')!;
    vol.value = String(Math.round(audio.level * 100));
    num.textContent = vol.value;
    vol.addEventListener('input', () => {
      audio.setVolume(Number(vol.value) / 100);
      num.textContent = vol.value;
      this.setSoundMode(audio.soundMode);
      audio.play('ui');
    });
    const music = this.pauseScreen.querySelector<HTMLInputElement>('[data-music]')!;
    const musicNum = this.pauseScreen.querySelector<HTMLElement>('[data-musicnum]')!;
    music.value = String(Math.round(audio.musicLevel * 100));
    musicNum.textContent = music.value;
    music.addEventListener('input', () => {
      audio.setMusicVolume(Number(music.value) / 100);
      musicNum.textContent = music.value;
    });
    this.pauseScreen
      .querySelector<HTMLButtonElement>('[data-mute]')!
      .addEventListener('click', () => this.setSoundMode(audio.cycleMode()));
    this.setSoundMode(audio.soundMode);
    this.pauseScreen
      .querySelector<HTMLButtonElement>('[data-resume]')!
      .addEventListener('click', () => this.hooks.onResume());
    this.pauseScreen
      .querySelector<HTMLButtonElement>('[data-quit]')!
      .addEventListener('click', () => this.hooks.onQuit());
  }

  showPause(): void {
    const vol = this.pauseScreen.querySelector<HTMLInputElement>('[data-vol]');
    if (vol) vol.value = String(Math.round(audio.level * 100));
    this.setSoundMode(audio.soundMode);
    const kit = this.pauseScreen.querySelector<HTMLElement>('[data-kit]');
    const s = this.lastSnap;
    if (kit) {
      const row = (x: { name: string; level: number; color: string; shape: string; sprite?: string }): string =>
        `<div class="kit-row">${iconHtml(x.shape, x.color, x.sprite, 18)}<span class="kit-name">${escapeHtml(x.name)}</span><b>${x.level}</b></div>`;
      kit.innerHTML = s
        ? `<div class="kit-col">${s.weapons.map(row).join('')}</div><div class="kit-col">${s.passives.map(row).join('')}</div>`
        : '';
      kit.hidden = !s || s.weapons.length + s.passives.length === 0;
    }
    this.pauseScreen.hidden = false;
  }

  /** 소리 모드를 화면에 반영한다. */
  setSoundMode(mode: SoundMode): void {
    const btn = this.pauseScreen.querySelector<HTMLButtonElement>('[data-mute]');
    const label = this.pauseScreen.querySelector<HTMLElement>('[data-modelabel]');
    if (btn) {
      btn.innerHTML = glyph(mode === 'off' ? 'mute' : 'speaker', 2);
      btn.classList.toggle('off', mode === 'off');
    }
    if (label) label.textContent = mode === 'off' ? '끔' : mode === 'quiet' ? '알림만' : '전부';
    this.muteBadge.hidden = mode === 'full';
    this.muteBadge.textContent = mode === 'off' ? '소리 끔 · M' : '알림만 · M';
  }

  hidePause(): void {
    this.pauseScreen.hidden = true;
  }

  // ── 결과 ───────────────────────────────────────────────
  // 1.23: 쓰러진 도트 + 점수 크게 + 네 칸(생존·처치·레벨·보스)

  private buildResult(): void {
    this.resultScreen.innerHTML = `
      <div class="result-box">
        <div class="res-top">
          <span class="res-art" data-art></span>
          <div class="res-head">
            <div class="res-title" data-headline>쓰러졌다</div>
            <div class="res-char" data-char>-</div>
          </div>
        </div>
        <div class="score-big" data-score>0</div>
        <div class="res-stats">
          <div class="stat">${glyph('clock', 2)}<b data-surv>0:00</b><span>생존</span></div>
          <div class="stat">${glyph('skull', 2)}<b data-kills>0</b><span>처치</span></div>
          <div class="stat">${glyph('level', 2)}<b data-level>1</b><span>레벨</span></div>
          <div class="stat">${glyph('crown', 2)}<b data-boss>0</b><span>보스</span></div>
        </div>
        <div class="result-actions">
          <button class="btn" type="button" data-retry>다시 하기</button>
          <button class="btn ghost" type="button" data-menu>캐릭터 선택</button>
        </div>
        <div id="submit-status" data-status></div>
        <div class="result-actions" style="margin-top:10px" data-resubmit-wrap hidden>
          <button class="btn ghost" type="button" data-resubmit>점수 다시 전송</button>
        </div>
      </div>
    `;
    const q = <T extends HTMLElement>(s: string): T => this.resultScreen.querySelector(s) as T;
    this.submitStatus = q('[data-status]');
    this.resubmitBtn = q<HTMLButtonElement>('[data-resubmit]');
    q<HTMLButtonElement>('[data-retry]').addEventListener('click', () => this.hooks.onRetry());
    q<HTMLButtonElement>('[data-menu]').addEventListener('click', () => this.hooks.onQuit());
    this.resubmitBtn.addEventListener('click', () => this.hooks.onResubmit());
  }

  showResult(result: RunResult, characterName: string): void {
    const q = <T extends HTMLElement>(s: string): T => this.resultScreen.querySelector(s) as T;
    q('[data-headline]').textContent = '쓰러졌다';
    q('[data-boss]').textContent = `${result.bossesDown}`;
    q('[data-score]').textContent = result.score.toLocaleString('ko-KR');
    q('[data-char]').textContent = characterName;
    q('[data-surv]').textContent = formatTime(result.survivalSec);
    q('[data-kills]').textContent = result.kills.toLocaleString('ko-KR');
    q('[data-level]').textContent = String(result.level);
    // 쓰러진 모습 (없으면 도형)
    const c = this.characters.find((x) => x.id === result.characterId);
    const art = q('[data-art]');
    const src = c ? frameSrc(c, c.anim?.dead[0] ?? -1) : null;
    art.innerHTML = src ? `<img alt="" src="${escapeHtml(src)}">` : c ? iconHtml(c.shape, c.color, c.sprite, 48) : '';
    const img = art.querySelector('img');
    if (img) {
      const size = (): void => {
        img.width = img.naturalWidth * HERO_SCALE;
        img.height = img.naturalHeight * HERO_SCALE;
      };
      if (img.complete && img.naturalWidth) size();
      else img.onload = size;
    }
    this.setSubmitStatus('');
    this.setResubmitVisible(false);
    this.hideAll();
    this.resultScreen.hidden = false;
    this.setHudVisible(false);
  }

  setSubmitStatus(text: string, tone: 'ok' | 'error' | 'muted' = 'muted'): void {
    this.submitStatus.textContent = text;
    this.submitStatus.style.color =
      tone === 'ok' ? 'var(--green)' : tone === 'error' ? 'var(--danger)' : 'var(--muted)';
  }

  setResubmitVisible(v: boolean): void {
    const wrap = this.resultScreen.querySelector<HTMLElement>('[data-resubmit-wrap]');
    if (wrap) wrap.hidden = !v;
  }

  // ── 이름 띠 ────────────────────────────────────────────

  /** 화면 가운데 큰 띠. 조합하는 순간(고유 스킬 이름)과 엘리트 출현, 1.22 보스 등장(받은 그림이 옆에서 밀려 들어온다)에 쓴다. 저절로 사라진다 */
  showBanner(text: string, sub: string, color: string, kind: 'fusion' | 'notice' | 'boss', image?: string): void {
    window.clearTimeout(this.bannerTimer);
    this.banner.className = `banner ${kind}`;
    this.banner.style.setProperty('--c', color);
    const img = image ? `<img class="banner-img" alt="" src="${escapeHtml(image)}">` : '';
    this.banner.innerHTML = `${img}<div class="banner-text">${escapeHtml(text)}</div><div class="banner-sub">${escapeHtml(sub)}</div>`;
    this.banner.hidden = false;
    // 애니메이션을 처음부터 다시 틀기 위해 한 번 흔든다
    void this.banner.offsetWidth;
    this.banner.classList.add('play');
    this.bannerTimer = window.setTimeout(() => (this.banner.hidden = true), kind === 'boss' ? 3000 : kind === 'fusion' ? 2200 : 1800);
  }

  // ── 공통 ───────────────────────────────────────────────

  hideAll(): void {
    this.stopWalk();
    this.selectScreen.hidden = true;
    this.levelScreen.hidden = true;
    this.pauseScreen.hidden = true;
    this.resultScreen.hidden = true;
  }
}
